/**
 * Puente compartido React ↔ itemTooltip.js (character profile, drop log, etc.)
 * Mismo flujo que CI4: cargar script completo + patch apiFetch + hover por id64.
 */
import { installWebViewAuthBridge, refreshSessionFromToken } from '@/lib/axios';
import { assetUrl } from '@/lib/assetUrl';

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function patchItemTooltipApiFetch(): void {
  const tt = window.ItemTooltip;
  if (!tt || tt.__apiFetchPatched || typeof tt.apiFetch !== 'function') return;

  const original = tt.apiFetch.bind(tt);
  tt.apiFetch = async (path: string, options?: RequestInit) => {
    let res = await original(path, options);
    if (res.status === 401) {
      await refreshSessionFromToken();
      installWebViewAuthBridge();
      res = await original(path, options);
    }
    return res;
  };
  tt.__apiFetchPatched = true;
}

/** Como el <script src="itemTooltip.js"> de CI4 — espera la clase completa con renderCharacterProfile. */
export async function loadItemTooltipScript(): Promise<void> {
  installWebViewAuthBridge();

  if (window.ItemTooltip?.renderCharacterProfile) {
    window.ItemTooltip.baseUrl = window.__APPWEB_API_BASE__ || '/api/v1';
    return;
  }

  const src = assetUrl('/assets/js/itemTooltip.js');
  const base = src.split('?')[0];
  let existing = document.querySelector(`script[src^="${base}"]`);

  if (existing && !window.ItemTooltip?.renderCharacterProfile) {
    delete window.ItemTooltipAPI;
    delete window.ItemTooltip;
    delete window.switchInventoryTab;
    existing.remove();
    existing = null;
  }

  if (!existing) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('itemTooltip-script'));
      document.body.appendChild(script);
    });
  }

  for (let i = 0; i < 60; i++) {
    if (window.ItemTooltip?.renderCharacterProfile) {
      window.ItemTooltip.baseUrl = window.__APPWEB_API_BASE__ || '/api/v1';
      return;
    }
    await wait(50);
  }

  throw new Error('itemTooltip-not-available');
}

export async function ensureItemTooltipReady(): Promise<void> {
  installWebViewAuthBridge();
  await loadItemTooltipScript();
  patchItemTooltipApiFetch();
}

type TooltipHandler = (e: MouseEvent) => void;

interface AttachedTooltip {
  enter: TooltipHandler;
  move: TooltipHandler;
  leave: TooltipHandler;
}

/**
 * Equivalente a attachItemTooltips() en logsystem.php (CI4).
 * Usa getItemTooltipByID64 + showTooltip — mismo item stats que character profile.
 */
export function attachItemTooltipById64(
  root: ParentNode = document,
  selector = '.item-with-tooltip[data-id64]',
): () => void {
  const attached = new Map<Element, AttachedTooltip>();
  const tt = window.ItemTooltip;

  if (!tt?.getItemTooltipByID64) {
    return () => {};
  }

  root.querySelectorAll(selector).forEach((node) => {
    const el = node as HTMLElement;
    const id64 = el.getAttribute('data-id64');
    if (!id64 || id64 === 'null') return;

    const onEnter: TooltipHandler = async (e) => {
      try {
        const itemData = await tt.getItemTooltipByID64!(id64);
        if (itemData) {
          tt.showTooltip!(itemData, e.pageX, e.pageY);
        }
      } catch {
        /* ignore tooltip errors */
      }
    };

    const onMove: TooltipHandler = (e) => {
      tt.positionTooltip?.(e.pageX, e.pageY);
    };

    const onLeave: TooltipHandler = () => {
      tt.hideTooltip?.();
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    attached.set(el, { enter: onEnter, move: onMove, leave: onLeave });
  });

  return () => {
    attached.forEach((handlers, el) => {
      el.removeEventListener('mouseenter', handlers.enter);
      el.removeEventListener('mousemove', handlers.move);
      el.removeEventListener('mouseleave', handlers.leave);
    });
    attached.clear();
    window.ItemTooltip?.hideTooltip?.();
  };
}

declare global {
  interface Window {
    ItemTooltipAPI?: unknown;
    ItemTooltip?: {
      baseUrl: string;
      cache: Map<string, unknown>;
      apiFetch: (path: string, options?: RequestInit) => Promise<Response>;
      renderCharacterProfile: (charId: string | number, container: HTMLElement) => Promise<void>;
      getItemTooltipByID64?: (id64: string) => Promise<unknown>;
      showTooltip?: (item: unknown, x: number, y: number) => void;
      hideTooltip?: () => void;
      positionTooltip?: (x: number, y: number) => void;
      attachTooltipListeners?: (container: HTMLElement) => void;
      __apiFetchPatched?: boolean;
    };
    switchInventoryTab?: (charId: number, tab: string, btn: HTMLElement) => Promise<void>;
  }
}
